// Fatia S2: Contas conectadas (/app/:companyId/contas).
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Copy, Link2, Lock, Plus } from 'lucide-react'
import type { AdProvider } from '@impulso/shared'
import { errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { Empty, ErrorState, Loading, PageHead, PlatformLogo } from '../../ui'
import { useToast } from '../../ui/toast'
import { useConnections, useCreateConnectionLink, useDisconnect, useProviders, useStartConnection, useSyncConnection } from '../../features/connections/api'
import ConnectionCard from '../../features/connections/ConnectionCard'
import OAuthReturn from '../../features/connections/OAuthReturn'
import { brand } from '../../features/connections/format'
import '../../features/account/pages.css'

export default function Connections() {
  const company = useCompany()
  const canManage = useCan('company.manage_connections')
  const canSync = useCan('company.edit_drafts')
  const notify = useToast()
  const conns = useConnections(company.id)
  const providers = useProviders()
  const start = useStartConnection(company.id)
  const sync = useSyncConnection(company.id)
  const disconnect = useDisconnect(company.id)
  const [syncingId, setSyncingId] = useState<string | null>(null)

  const items = conns.data?.items ?? []
  const importing = items.some((c) => c.importing)

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Contas conectadas" sub="O Seu Impulso conversa com suas contas pelos canais oficiais do Google e da Meta. Você pode desconectar quando quiser." />

      <div className="callout">
        <Lock size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
        <span className="small">Nunca pedimos sua senha. O acesso é autorizado direto no Google e na Meta, e o dinheiro dos anúncios continua sendo cobrado por eles, no seu cartão. <Link className="strong" style={{ color: 'var(--primary)' }} to={`/app/${company.id}/contas/pagamento`}>Como pagar nas plataformas</Link></span>
      </div>

      <OAuthReturn companyId={company.id} />

      {importing && (
        <div className="callout green" role="status">
          <span className="small"><strong>Importando o histórico.</strong> Estamos trazendo os últimos 13 meses de resultados. Os números do painel vão aparecendo em alguns minutos.</span>
        </div>
      )}

      <section>
        <div className="section-label">Anúncios</div>
        {conns.isLoading ? (
          <Loading lines={4} />
        ) : conns.error ? (
          <ErrorState error={conns.error} onRetry={() => void conns.refetch()} />
        ) : items.length === 0 ? (
          <Empty title="Nenhuma conta conectada ainda">
            <p className="muted">Conecte o Google Ads ou a Meta (Instagram e Facebook) para ver seus resultados e receber sugestões.</p>
          </Empty>
        ) : (
          <div className="grid g-fit">
            {items.map((c) => (
              <ConnectionCard
                key={c.id}
                connection={c}
                canManage={canManage}
                canSync={canSync}
                syncing={syncingId === c.id && sync.isPending}
                onSync={() => {
                  setSyncingId(c.id)
                  sync.mutate(c.id, {
                    onSuccess: () => notify('Sincronizando… os números atualizam em instantes.'),
                    onError: (e) => notify(errorMessage(e)),
                  })
                }}
                onReconnect={() => start.mutate(c.provider)}
                onDisconnect={() => disconnect.mutate(c.id, { onSuccess: () => notify('Conta desconectada.'), onError: (e) => notify(errorMessage(e)) })}
              />
            ))}
          </div>
        )}
      </section>

      {canManage && (
        <section>
          <div className="section-label">Conectar uma conta</div>
          {providers.isLoading ? (
            <Loading lines={2} />
          ) : providers.error ? (
            <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />
          ) : (
            <div className="grid g-fit">
              {(providers.data?.items ?? []).map((p) => (
                <div key={p.provider} className="card" style={{ display: 'flex', gap: 14 }}>
                  <PlatformLogo platform={brand(p.provider)} size="lg" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3>{p.name}</h3>
                    <div className="small muted">{p.description}</div>
                    <div className="small mt-8">O Seu Impulso poderá:</div>
                    <ul className="small muted" style={{ paddingLeft: 18, margin: '4px 0 0' }}>
                      {p.permissions.map((perm) => <li key={perm}>{perm}</li>)}
                    </ul>
                    <button className="btn primary sm mt-12" disabled={start.isPending} onClick={() => start.mutate(p.provider)}>
                      <Plus size={14} /> {start.isPending && start.variables === p.provider ? 'Abrindo…' : 'Conectar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {start.error && <div className="form-error mt-12" role="alert">{errorMessage(start.error)}</div>}
        </section>
      )}

      <section className="card row wrap" style={{ gap: 14 }}>
        <span className="ic purple"><BarChart3 size={20} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>Google Analytics</h3>
          <div className="small muted">Veja as visitas e os eventos-chave do seu site junto com os anúncios.</div>
        </div>
        <Link className="btn sm" to={`/app/${company.id}/trackeamento/analytics`}>Abrir Analytics do site</Link>
      </section>

      {canManage && <ShareLinkCard companyId={company.id} companyName={company.name} providers={(providers.data?.items ?? []).map((p) => ({ provider: p.provider, name: p.name }))} />}
    </div>
  )
}

function ShareLinkCard({ companyId, companyName, providers }: { companyId: string; companyName: string; providers: Array<{ provider: AdProvider; name: string }> }) {
  const create = useCreateConnectionLink(companyId)
  const notify = useToast()
  const [provider, setProvider] = useState<AdProvider | ''>('')
  const link = create.data

  async function copy() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link.url)
      notify('Link copiado. Envie para quem cuida das contas de anúncio.')
    } catch {
      notify('Não foi possível copiar. Selecione o link e copie manualmente.')
    }
  }

  return (
    <section className="card">
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <Link2 size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>Pedir para outra pessoa conectar</h3>
          <p className="small muted mt-8">Gere um link para o dono das contas de anúncio de {companyName} conectar sem precisar criar login no Seu Impulso. O link vale por 7 dias e pode ser usado uma vez.</p>
          <div className="row wrap mt-12" style={{ gap: 8 }}>
            <label className="small" htmlFor="link-provider">Plataforma</label>
            <select id="link-provider" className="input" style={{ width: 'auto', minWidth: 0, maxWidth: '100%' }} value={provider} onChange={(e) => setProvider(e.target.value as AdProvider | '')}>
              <option value="">Qualquer uma</option>
              {providers.map((p) => <option key={p.provider} value={p.provider}>{p.name}</option>)}
            </select>
            <button className="btn sm" disabled={create.isPending} onClick={() => create.mutate(provider || null)}>{create.isPending ? 'Gerando…' : 'Gerar link'}</button>
          </div>
          {create.error && <div className="form-error mt-12" role="alert">{errorMessage(create.error)}</div>}
          {link && (
            <div className="mt-12 stack" style={{ gap: 8 }}>
              <div className="row" style={{ gap: 8 }}>
                <input className="input" readOnly value={link.url} aria-label="Link de conexão" onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, minWidth: 0 }} />
                <button className="btn sm" onClick={() => void copy()}><Copy size={14} /> Copiar</button>
              </div>
              <span className="small muted">Válido até {new Date(link.expiresAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.</span>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
